import { useState } from "react"
import { Alert, Button, Flex } from "antd"
import { CloseOutlined, RedoOutlined, UndoOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { DeleteConfirmation } from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import { Text } from "ui/Typography"
import styles from "./GeneratedSlotsList.module.css"

const GeneratedSlotsList = ({ slots, onRemoveSlot, onUndo, onRedo, onClearAll, canUndo, canRedo, onConfirm, confirmResult }) => {
  const { t } = useTranslation()
  const [isClearAllOpen, setIsClearAllOpen] = useState(false)

  const confirmMessage = (() => {
    if (!confirmResult) return null
    if (confirmResult.added === 0) return t("experiences.ticketed_schedule.slots_all_duplicates")
    if (confirmResult.skipped > 0) return t("experiences.ticketed_schedule.slots_added_with_skipped", { added: confirmResult.added, skipped: confirmResult.skipped })
    return t("experiences.ticketed_schedule.slots_added", { count: confirmResult.added })
  })()

  return (
    <Flex vertical className={styles.previewPanel}>
      <Text className={styles.previewTitle}>
        {t("experiences.ticketed_schedule.generated_title")}
        {slots.length > 0 && <span className={styles.slotCount}> ({slots.length})</span>}
      </Text>

      {confirmMessage && (
        <Alert
          type={confirmResult.added > 0 ? "success" : "warning"}
          message={confirmMessage}
          showIcon
          className={styles.confirmAlert}
        />
      )}

      {slots.length === 0 ? (
        <Flex justify="center" align="center" className={styles.emptyState}>
          <Text className={styles.emptyText}>{t("experiences.ticketed_schedule.no_preview")}</Text>
        </Flex>
      ) : (
        <Flex vertical className={styles.slotsContainer}>
          <Flex align="center" justify="flex-end" gap={4} className={styles.actionsRow}>
            <Button type="link" size="small" icon={<UndoOutlined />} disabled={!canUndo} onClick={onUndo}>
              {t("experiences.ticketed_schedule.undo")}
            </Button>
            <Button type="link" size="small" icon={<RedoOutlined />} disabled={!canRedo} onClick={onRedo}>
              {t("experiences.ticketed_schedule.redo")}
            </Button>
            <Button type="link" size="small" danger onClick={() => setIsClearAllOpen(true)}>
              {t("experiences.ticketed_schedule.clear_all")}
            </Button>
          </Flex>

          <Flex vertical gap={4} className={styles.slotList}>
            {slots.map((slot) => (
              <Flex key={slot.id} align="center" justify="space-between" className={styles.slotRow}>
                <Text className={styles.slotLabel}>{slot.label}</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  className={styles.removeSlotBtn}
                  onClick={() => onRemoveSlot(slot.id)}
                />
              </Flex>
            ))}
          </Flex>

          <div className={styles.confirmFooter}>
            <Button type="primary" shape="round" size="large" onClick={onConfirm}>
              {t("experiences.ticketed_schedule.confirm_schedule")}
            </Button>
          </div>
        </Flex>
      )}

      <DeleteConfirmation
        visible={isClearAllOpen}
        title={t("experiences.ticketed_schedule.clear_all_confirm_title")}
        content={t("experiences.ticketed_schedule.clear_all_confirm_content")}
        confirmText={t("experiences.ticketed_schedule.clear_all")}
        cancelText={t("global.cancel")}
        onCancel={() => setIsClearAllOpen(false)}
        onConfirm={() => {
          onClearAll()
          setIsClearAllOpen(false)
        }}
      />
    </Flex>
  )
}

GeneratedSlotsList.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  onRemoveSlot: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  canUndo: PropTypes.bool.isRequired,
  canRedo: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmResult: PropTypes.shape({
    added: PropTypes.number,
    skipped: PropTypes.number
  })
}

GeneratedSlotsList.defaultProps = {
  confirmResult: null
}

export default GeneratedSlotsList
